import { useDndMonitor } from '@dnd-kit/core';
import {
  ArrowUp01Icon,
  Calendar03Icon,
  Cancel01Icon,
  CheckListIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  CALENDAR_VIEW_MODE,
  type CalendarEvent,
  CalendarScheduler,
  type CalendarViewMode,
  type EventRenderProps,
  RadioGroup,
} from '@pique/component';
import { enhancedNotify } from '@pique/core/components/shared/notification-utils';
import {
  ActionItemsService,
  DEFAULT_PLANNED_FOCUS_MINUTES,
  getPlannedFocusMinutes,
} from '@pique/core/modules/properties/services/action-items-service';
import type { GoogleCalendarEvent } from '@pique/core/modules/properties/services/calendar-types';
import {
  ACTION_ITEM_STATUS,
  type SerializedTodoItem,
} from '@pique/core/modules/properties/services/schema';
import { isActionItemCompleted } from '@pique/core/modules/properties/services/utils';
import { posthog } from '@pique/core/posthog';
import { useLiveData, useService } from '@toeverything/infra';
import { format } from 'date-fns';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useAtom } from 'jotai';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type FocusZoneDisplayMode,
  focusZoneDisplayModeAtom,
} from '../../../../../atoms';
import { calendarEventsAtom } from '../../../../../components/pique/setting-modal/atoms';
import { ActionItemFocusList } from './ActionItemFocusList';
import * as styles from './ActionItemsCalendar.css';
import {
  buildSlotDate,
  getEventDurationMinutes,
  getEventStartParts,
  getNavigationDateFromEventId,
  isRecurringEventId,
  parseCalendarSlotId,
} from './focus-mode-utils';
import { TodoItemComponent } from './TodoItemComponent';

export const FOCUS_ZONE_ID = 'focus-zone';
const CALENDAR_DROP_PREFIX = 'calendar-slot:';
const DRAG_SNAP_MINUTES = 30;
const SCROLL_EDGE_PX = 20;
const SCROLL_SPEED_PX = 6;
const GRID_HOVER_PADDING_PX = 24;

const formatDateKey = (date: Date): string => {
  return format(date, 'yyyy-MM-dd');
};

const getWeekDates = (date: Date): Date[] => {
  const start = new Date(date);
  start.setDate(start.getDate() - 1);
  start.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const next = new Date(start);
    next.setDate(start.getDate() + i);
    return next;
  });
};

function convertGoogleEventToCalendarEvent(
  event: GoogleCalendarEvent
): CalendarEvent {
  const startDate = new Date(event.startTime);
  const endDate = new Date(event.endTime);

  return {
    id: event.id,
    title: event.summary || '(No Title)',
    date: format(startDate, 'yyyy-MM-dd'),
    startTime: format(startDate, 'HH:mm'),
    endTime: format(endDate, 'HH:mm'),
    category: 'meeting',
    description: event.description || undefined,
    readonly: true,
  };
}

function convertTodoToUnscheduledEvent(
  item: SerializedTodoItem,
  targetDateStr: string,
  index: number,
  previousItems: SerializedTodoItem[],
  isPreview: boolean = false
): CalendarEvent {
  const plannedMinutes = getPlannedFocusMinutes(item);

  if (item.scheduledAt) {
    const scheduled = new Date(item.scheduledAt);
    const endTime = new Date(scheduled.getTime() + plannedMinutes * 60000);
    return {
      id: item.id,
      title: item.title || 'Untitled Task',
      date: format(scheduled, 'yyyy-MM-dd'),
      startTime: format(scheduled, 'HH:mm'),
      endTime: format(endTime, 'HH:mm'),
      category: 'work',
      description: item.context || undefined,
      readonly: isPreview || undefined,
      isPreview: isPreview || undefined,
    };
  }

  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  let offsetMinutes = 0;
  for (let i = 0; i < index; i++) {
    const prev = previousItems[i];
    if (prev && !prev.scheduledAt) {
      offsetMinutes += getPlannedFocusMinutes(prev);
    }
  }

  const baseStartMinutes = Math.max(0, currentMinutes);
  const startMinutes = baseStartMinutes + offsetMinutes;
  const startHour = Math.floor(startMinutes / 60);
  const startMin = startMinutes % 60;
  const endMinutes = startMinutes + plannedMinutes;
  const endHour = Math.floor(endMinutes / 60);
  const endMin = endMinutes % 60;

  return {
    id: item.id,
    title: item.title || 'Untitled Task',
    date: targetDateStr,
    startTime: `${startHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}`,
    endTime: `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`,
    category: 'work',
    description: item.context || undefined,
    readonly: isPreview || undefined,
    isPreview: isPreview || undefined,
  };
}

function getEventStartIso(
  event: Pick<CalendarEvent, 'date' | 'startTime'>
): string | null {
  const parts = getEventStartParts(event);
  if (!parts) return null;
  const { year, month, day, hour, minute } = parts;
  const scheduledDate = new Date(year, month - 1, day, hour, minute, 0, 0);
  if (isNaN(scheduledDate.getTime())) return null;
  return scheduledDate.toISOString();
}

function generateRepeatInstances(
  item: SerializedTodoItem,
  weekDateKeys: Set<string>
): CalendarEvent[] {
  const config = item.repeatConfig;
  if (!config) return [];
  const timeParts = config.time.split(':');
  const hourStr = timeParts[0];
  const minStr = timeParts[1];
  const hour = Number(hourStr) || 8;
  const minute = Number(minStr) || 0;
  const plannedMinutes = getPlannedFocusMinutes(item);
  const events: CalendarEvent[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayMs = today.getTime();

  const itemScheduledMs = item.scheduledAt
    ? (() => {
        const d = new Date(item.scheduledAt);
        if (isNaN(d.getTime())) return todayMs;
        d.setHours(0, 0, 0, 0);
        return d.getTime();
      })()
    : todayMs;

  const cutoffMs = Math.max(todayMs, itemScheduledMs);

  for (const dateStr of weekDateKeys) {
    const dateParts = dateStr.split('-').map(Number);
    const year = dateParts[0] ?? 0;
    const month = dateParts[1] ?? 1;
    const day = dateParts[2] ?? 1;
    const eventDate = new Date(year, month - 1, day);
    if (eventDate.getTime() <= cutoffMs) continue;
    const dayOfWeek = eventDate.getDay();
    if (config.frequency === 'weekdays' && (dayOfWeek === 0 || dayOfWeek === 6))
      continue;
    if (config.frequency === 'weekly' && dayOfWeek !== 1) continue;
    const endMinutes = hour * 60 + minute + plannedMinutes;
    const endHour = Math.floor(endMinutes / 60);
    const endMin = endMinutes % 60;
    events.push({
      id: `${item.id}:repeat:${dateStr}`,
      title: item.title || 'Untitled Task',
      date: dateStr,
      startTime: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
      endTime: `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`,
      category: 'work',
      description: item.context || undefined,
    });
  }
  return events;
}

interface FocusDropZoneProps {
  focusedItems: SerializedTodoItem[];
  onItemComplete?: (itemId: string, pageId: string) => void;
  dropActive?: boolean;
  dropIndicatorItem?: SerializedTodoItem | null;
}

export const FocusDropZone: React.FC<FocusDropZoneProps> = ({
  focusedItems: focusedItemsProp,
  onItemComplete,
  dropActive = false,
  dropIndicatorItem = null,
}) => {
  const focusZoneRef = useRef<HTMLDivElement | null>(null);
  const hasTrackedHoverRef = useRef(false);
  const actionItemsService = useService(ActionItemsService);
  const actionItemsByPage = useLiveData(actionItemsService.actionItems$);
  const actionItemById = useMemo(() => {
    const map = new Map<string, SerializedTodoItem>();
    (Object.values(actionItemsByPage) as SerializedTodoItem[][]).forEach(
      items => {
        items.forEach(item => {
          map.set(item.id, item);
        });
      }
    );
    return map;
  }, [actionItemsByPage]);
  const [calendarEventsFromAtom] = useAtom(calendarEventsAtom);
  const calendarEvents = calendarEventsFromAtom;
  const [displayMode, setDisplayMode] = useAtom(focusZoneDisplayModeAtom);
  const [calendarDate, setCalendarDate] = useState(() => new Date());
  const [calendarViewMode, setCalendarViewMode] = useState<CalendarViewMode>(
    CALENDAR_VIEW_MODE.DAY
  );
  const [scrollToTime, setScrollToTime] = useState<Date | undefined>();
  const [draftEvent, setDraftEvent] = useState<CalendarEvent | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const draftInputRef = useRef<HTMLInputElement>(null);
  const [dragPreview, setDragPreview] = useState<{
    itemId: string;
    dateStr: string;
    hour: number;
    minute: number;
  } | null>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [draggingFromList, setDraggingFromList] = useState(false);
  const dragPreviewKeyRef = useRef<string | null>(null);
  const dragPreviewRef = useRef<typeof dragPreview>(null);
  const committedPreviewRef = useRef<{
    itemId: string;
    scheduledAt: string;
    plannedDuration: number;
  } | null>(null);
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);
  const activeDragIdRef = useRef<string | null>(null);
  const activeDragIsCalendarEventRef = useRef(false);
  const dragWithinGridRef = useRef(false);
  const pointerMoveHandlerRef = useRef<((event: PointerEvent) => void) | null>(
    null
  );
  const scrollVelocityRef = useRef(0);
  const scrollRafRef = useRef<number | null>(null);
  const dragHoveringRef = useRef(false);
  const calendarDateTouchedRef = useRef(false);
  const prevHasItemsRef = useRef(false);
  const reduceMotion = useReducedMotion();
  const dragDefaultedRef = useRef(false);
  const notifiedScheduledItemsRef = useRef<Set<string>>(new Set());
  const focusedItemId = useLiveData(actionItemsService.focusedItemId$);
  const [dragHoveringFocusZone, setDragHoveringFocusZone] = useState(false);
  const [highlightedEventId, setHighlightedEventId] = useState<string | null>(
    null
  );
  const [pendingRecurringUpdate, setPendingRecurringUpdate] = useState<{
    itemId: string;
    newTime: string;
    newDuration?: number;
  } | null>(null);

  const focusedItems = focusedItemsProp;
  const isFocusZoneActive = dropActive || dragHoveringFocusZone;

  const previewItem = useMemo(() => {
    if (!dragPreview) return null;
    const base = actionItemById.get(dragPreview.itemId);
    if (!base) return null;
    const dateParts = dragPreview.dateStr.split('-').map(Number);
    const year = dateParts[0] ?? 0;
    const month = dateParts[1] ?? 1;
    const day = dateParts[2] ?? 1;
    if (![year, month, day].every(n => Number.isFinite(n))) return null;
    const scheduledDate = new Date(
      year,
      month - 1,
      day,
      dragPreview.hour,
      dragPreview.minute,
      0,
      0
    );
    if (isNaN(scheduledDate.getTime())) return null;
    return {
      ...base,
      scheduledAt: scheduledDate.toISOString(),
      plannedDuration: getPlannedFocusMinutes(base),
    };
  }, [actionItemById, dragPreview]);

  const displayFocusedItems = useMemo(() => {
    // 1. Handle "Committed" State (Optimistic Update after Drop)
    // When we drop, we set committedPreviewRef and clear the dragPreview.
    // We must keep showing the item at the committed position until the backend update propagates.
    const committed = committedPreviewRef.current;
    if (committed && !previewItem) {
      const actualItem = focusedItems.find(
        item => item.id === committed.itemId
      );

      // If the item exists and its data matches our commit, the sync is done.
      if (actualItem?.scheduledAt === committed.scheduledAt) {
        committedPreviewRef.current = null;
        return focusedItems;
      }

      // If the item exists but data is stale, we override it optimistically.
      if (actualItem) {
        return focusedItems.map(item =>
          item.id === committed.itemId
            ? {
                ...item,
                scheduledAt: committed.scheduledAt,
                plannedDuration: committed.plannedDuration,
              }
            : item
        );
      }

      // If the item is NOT in focusedItems (e.g. dragging new item into calendar),
      // we must pull the original item data and append it with the committed schedule.
      // We can try to find it in the global map or just rely on the committed data + minimal placeholder.
      // Since we don't have easy access to the full item if it's not in focusedItems without
      // adding more dependencies (which causes re-renders), we will try to find it in actionItemById if possible,
      // or assume the user just needs to see it persist.
      const rawItem = actionItemById.get(committed.itemId);
      if (rawItem) {
        return [
          ...focusedItems,
          {
            ...rawItem,
            isFocused: true,
            status: ACTION_ITEM_STATUS.IN_PROGRESS,
            scheduledAt: committed.scheduledAt,
            plannedDuration: committed.plannedDuration,
          },
        ];
      }
    }

    // 2. Handle "Active Drag" State (Preview during Drag)
    if (!previewItem) return focusedItems;

    // Replace existing item if present
    const replaced = focusedItems.map(item =>
      item.id === previewItem.id ? previewItem : item
    );

    // If it was in the list, return the list with the replacement
    if (replaced.some(item => item.id === previewItem.id)) return replaced;

    // If it wasn't in the list (new item drag), append it
    return [...replaced, previewItem];
  }, [focusedItems, previewItem, actionItemById]);

  const hasItems = focusedItems.length > 0;
  const isEmptyState = !hasItems && !isFocusZoneActive;

  useEffect(() => {
    dragPreviewRef.current = dragPreview;
  }, [dragPreview]);

  const setDragPreviewSlot = useCallback(
    (itemId: string, dateStr: string, hour: number, minute: number) => {
      const nextKey = `${itemId}:${dateStr}:${hour}:${minute}`;
      if (dragPreviewKeyRef.current === nextKey) return;
      dragPreviewKeyRef.current = nextKey;
      setDragPreview({ itemId, dateStr, hour, minute });
    },
    []
  );

  const getSnappedNowSlot = useCallback(() => {
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const snapMinutes =
      Number.isFinite(DRAG_SNAP_MINUTES) && DRAG_SNAP_MINUTES > 0
        ? DRAG_SNAP_MINUTES
        : 30;
    const snappedMinutes =
      Math.floor(currentMinutes / snapMinutes) * snapMinutes;
    const hour = Math.floor(snappedMinutes / 60);
    const minute = snappedMinutes % 60;
    return { dateStr: formatDateKey(now), hour, minute };
  }, []);

  const clampSlotToNow = useCallback(
    (slot: { dateStr: string; hour: number; minute: number }) => {
      const candidate = buildSlotDate(slot.dateStr, slot.hour, slot.minute);
      if (!candidate) return slot;
      const currentSlot = getSnappedNowSlot();
      const currentSlotDate = buildSlotDate(
        currentSlot.dateStr,
        currentSlot.hour,
        currentSlot.minute
      );
      if (currentSlotDate && candidate.getTime() < currentSlotDate.getTime()) {
        return currentSlot;
      }
      return slot;
    },
    [getSnappedNowSlot]
  );

  const getDefaultAnchorSlot = useCallback(() => {
    const baseDate =
      calendarDate instanceof Date && !isNaN(calendarDate.getTime())
        ? calendarDate
        : new Date();
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const snapMinutes =
      Number.isFinite(DRAG_SNAP_MINUTES) && DRAG_SNAP_MINUTES > 0
        ? DRAG_SNAP_MINUTES
        : 30;
    const maxMinutes = 24 * 60 - snapMinutes;
    const snappedMinutes = Math.min(
      Math.ceil(currentMinutes / snapMinutes) * snapMinutes,
      maxMinutes
    );
    const hour = Math.floor(snappedMinutes / 60);
    const minute = snappedMinutes % 60;
    return { dateStr: formatDateKey(baseDate), hour, minute };
  }, [calendarDate]);

  const setFocusZoneRef = useCallback(
    (node: HTMLDivElement | null) => {
      focusZoneRef.current = node;
    },
    []
  );

  useEffect(() => {
    const prevHasItems = prevHasItemsRef.current;
    prevHasItemsRef.current = hasItems;
    if (hasItems) return;
    if (prevHasItems) {
      calendarDateTouchedRef.current = false;
    }
    if (calendarDateTouchedRef.current) return;
    const today = new Date();
    const todayKey = format(today, 'yyyy-MM-dd');
    const currentKey = format(calendarDate, 'yyyy-MM-dd');
    if (todayKey !== currentKey) {
      setCalendarDate(today);
      setScrollToTime(today);
    }
  }, [calendarDate, hasItems]);

  const handleMouseEnter = useCallback(() => {
    if (!hasItems && !hasTrackedHoverRef.current) {
      hasTrackedHoverRef.current = true;
    }
  }, [hasItems]);

  const handleMouseLeave = useCallback(() => {
    hasTrackedHoverRef.current = false;
  }, []);

  const combinedCalendarEvents = useMemo(() => {
    const targetDateStr = format(calendarDate, 'yyyy-MM-dd');
    const weekDateKeys =
      calendarViewMode === CALENDAR_VIEW_MODE.WEEK
        ? new Set(getWeekDates(calendarDate).map(date => formatDateKey(date)))
        : new Set([targetDateStr]);
    const previewId = previewItem?.id;

    const googleEvents = calendarEvents
      .filter(e => {
        if (!e.startTime || !e.endTime) return false;
        const eventDateStr = format(new Date(e.startTime), 'yyyy-MM-dd');
        return weekDateKeys.has(eventDateStr);
      })
      .map(convertGoogleEventToCalendarEvent);

    const scheduledTodoEvents = displayFocusedItems
      .filter(item => {
        if (!item.scheduledAt) return false;
        const scheduled = new Date(item.scheduledAt);
        if (isNaN(scheduled.getTime())) return false;
        const scheduledDateStr = format(scheduled, 'yyyy-MM-dd');
        return weekDateKeys.has(scheduledDateStr);
      })
      .map(item =>
        convertTodoToUnscheduledEvent(
          item,
          targetDateStr,
          0,
          [],
          item.id === previewId
        )
      );

    const unscheduledItems = displayFocusedItems.filter(item => {
      if (item.scheduledAt) return false;
      if (
        draggingFromList &&
        activeDragId &&
        !dragPreview &&
        item.id === activeDragId
      ) {
        return false;
      }
      return true;
    });

    const unscheduledTodoEvents = unscheduledItems.map((item, index) =>
      convertTodoToUnscheduledEvent(
        item,
        targetDateStr,
        index,
        unscheduledItems,
        item.id === previewId
      )
    );

    const repeatEvents = displayFocusedItems.flatMap(item =>
      generateRepeatInstances(item, weekDateKeys)
    );

    const combined = [
      ...googleEvents,
      ...scheduledTodoEvents,
      ...unscheduledTodoEvents,
      ...repeatEvents,
    ];
    if (draftEvent) {
      combined.push(draftEvent);
    }
    return combined;
  }, [
    calendarEvents,
    displayFocusedItems,
    calendarDate,
    previewItem?.id,
    calendarViewMode,
    draftEvent,
    activeDragId,
    dragPreview,
    draggingFromList,
  ]);

  const updateScheduledFocusItem = useCallback(
    (itemId: string, scheduledDate: Date, plannedMinutes: number) => {
      const scheduledAt = scheduledDate.toISOString();
      actionItemsService.updateById(itemId, {
        isFocused: true,
        status: ACTION_ITEM_STATUS.IN_PROGRESS,
        scheduledAt,
        plannedDuration: plannedMinutes,
      });
      posthog.capture('action_item_scheduled', {
        item_id: itemId,
        scheduled_at: scheduledAt,
        planned_duration: plannedMinutes,
      });
    },
    [actionItemsService]
  );

  const clearScheduledFocusItem = useCallback(
    (itemId: string) => {
      actionItemsService.updateById(itemId, {
        scheduledAt: undefined,
        plannedDuration: undefined,
        isFocused: false,
        status: ACTION_ITEM_STATUS.TODO,
      });
      try {
        if (actionItemsService.focusedItemId$.value === itemId) {
          actionItemsService.setFocusedItem(null);
        }
      } catch {}
    },
    [actionItemsService]
  );

  const handleEventUpdate = useCallback(
    (event: CalendarEvent) => {
      const isRecurring = isRecurringEventId(event.id);
      const baseItemId = isRecurring ? event.id.split(':repeat:')[0] : event.id;
      const isTodoItem = focusedItems.some(item => item.id === baseItemId);
      if (!isTodoItem) return;

      const durationMinutes = getEventDurationMinutes(event);
      if (!durationMinutes) return;
      const startParts = getEventStartParts(event);
      if (!startParts) return;
      const clamped = clampSlotToNow({
        dateStr: event.date,
        hour: startParts.hour,
        minute: startParts.minute,
      });
      const scheduledDate = buildSlotDate(
        clamped.dateStr,
        clamped.hour,
        clamped.minute
      );
      if (!scheduledDate) return;

      if (!baseItemId) return;

      if (isRecurring) {
        const newTime = `${String(clamped.hour).padStart(2, '0')}:${String(clamped.minute).padStart(2, '0')}`;
        setPendingRecurringUpdate({ itemId: baseItemId, newTime });
        return;
      }

      updateScheduledFocusItem(baseItemId, scheduledDate, durationMinutes);
    },
    [clampSlotToNow, focusedItems, updateScheduledFocusItem]
  );

  const handleConfirmRecurringUpdate = useCallback(() => {
    if (!pendingRecurringUpdate) return;
    const { itemId, newTime } = pendingRecurringUpdate;
    const item = actionItemsService.findById(itemId);
    if (!item?.repeatConfig) {
      setPendingRecurringUpdate(null);
      return;
    }
    actionItemsService.updateById(itemId, {
      repeatConfig: {
        ...item.repeatConfig,
        time: newTime,
      },
    });
    setPendingRecurringUpdate(null);
  }, [actionItemsService, pendingRecurringUpdate]);

  const handleCancelRecurringUpdate = useCallback(() => {
    setPendingRecurringUpdate(null);
  }, []);

  const handleEventDelete = useCallback(
    (id: string) => {
      const isTodoItem = focusedItems.some(item => item.id === id);
      if (!isTodoItem) return;
      clearScheduledFocusItem(id);
    },
    [clearScheduledFocusItem, focusedItems]
  );

  const clearDraftEvent = useCallback(() => {
    setDraftEvent(null);
    setDraftTitle('');
  }, []);

  const commitDraftEvent = useCallback(() => {
    if (!draftEvent) return;
    const title = draftTitle.trim();
    if (!title) {
      clearDraftEvent();
      return;
    }

    const startParts = getEventStartParts(draftEvent);
    if (!startParts) return;
    const durationMinutes =
      getEventDurationMinutes(draftEvent) ?? DEFAULT_PLANNED_FOCUS_MINUTES;
    const clamped = clampSlotToNow({
      dateStr: draftEvent.date,
      hour: startParts.hour,
      minute: startParts.minute,
    });
    const scheduledDate = buildSlotDate(
      clamped.dateStr,
      clamped.hour,
      clamped.minute
    );
    if (!scheduledDate) return;

    const created = actionItemsService.createItem({
      title,
      status: ACTION_ITEM_STATUS.TODO,
      priorityRank: 1,
      progress: 0,
      remindLater: [],
      dueDate: null,
      closeLoop: '',
    });

    updateScheduledFocusItem(created.id, scheduledDate, durationMinutes);
    clearDraftEvent();
  }, [
    actionItemsService,
    clampSlotToNow,
    clearDraftEvent,
    draftEvent,
    draftTitle,
    updateScheduledFocusItem,
  ]);

  const handleDraftKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commitDraftEvent();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        clearDraftEvent();
      }
    },
    [clearDraftEvent, commitDraftEvent]
  );

  const handleDraftBlur = useCallback(() => {
    if (!draftTitle.trim()) {
      clearDraftEvent();
    }
  }, [clearDraftEvent, draftTitle]);

  const handleEventCreate = useCallback(
    (event: Omit<CalendarEvent, 'id'>) => {
      const startParts = getEventStartParts({
        date: event['date'],
        startTime: event['startTime'],
      });
      if (!startParts) return;

      const clamped = clampSlotToNow({
        dateStr: event['date'],
        hour: startParts.hour,
        minute: startParts.minute,
      });
      const startDate = buildSlotDate(
        clamped.dateStr,
        clamped.hour,
        clamped.minute
      );
      if (!startDate) return;

      if (calendarViewMode === CALENDAR_VIEW_MODE.WEEK) {
        calendarDateTouchedRef.current = true;
        setCalendarDate(startDate);
        setScrollToTime(startDate);
        setCalendarViewMode(CALENDAR_VIEW_MODE.DAY);
        return;
      }

      const durationMinutes =
        getEventDurationMinutes({
          startTime: event['startTime'],
          endTime: event['endTime'],
        }) ?? DEFAULT_PLANNED_FOCUS_MINUTES;
      const endDate = new Date(startDate.getTime() + durationMinutes * 60_000);
      setDraftEvent({
        id: crypto.randomUUID(),
        title: '',
        date: clamped.dateStr,
        startTime: format(startDate, 'HH:mm'),
        endTime: format(endDate, 'HH:mm'),
        category: event['category'],
        readonly: true,
      });
      setDraftTitle('');

      calendarDateTouchedRef.current = true;
      setScrollToTime(startDate);
      setCalendarDate(startDate);
    },
    [calendarViewMode, clampSlotToNow]
  );

  const itemsMap = useMemo(() => {
    const map = new Map<string, SerializedTodoItem>();
    displayFocusedItems.forEach(item => map.set(item.id, item));
    return map;
  }, [displayFocusedItems]);

  const getItemFromEventId = useCallback(
    (eventId: string): SerializedTodoItem | null => {
      if (eventId.includes(':repeat:')) {
        const baseId = eventId.split(':repeat:')[0];
        if (!baseId) return null;
        return itemsMap.get(baseId) ?? null;
      }
      return itemsMap.get(eventId) ?? null;
    },
    [itemsMap]
  );

  const handleClearSchedule = useCallback(
    (itemId: string) => {
      clearScheduledFocusItem(itemId);
    },
    [clearScheduledFocusItem]
  );

  const handleExpandToWeek = useCallback(
    (itemId: string) => {
      const item = actionItemById.get(itemId);
      if (item?.scheduledAt) {
        const scheduledDate = new Date(item.scheduledAt);
        if (!isNaN(scheduledDate.getTime())) {
          calendarDateTouchedRef.current = true;
          setCalendarDate(scheduledDate);
          setScrollToTime(scheduledDate);
        }
      }
      setCalendarViewMode(CALENDAR_VIEW_MODE.WEEK);
      setHighlightedEventId(itemId);
      setTimeout(() => setHighlightedEventId(null), 2000);
    },
    [actionItemById]
  );

  const handleCollapseToDay = useCallback(
    (eventId: string) => {
      const baseItemId = eventId.includes(':repeat:')
        ? eventId.split(':repeat:')[0]
        : eventId;
      if (!baseItemId) return;
      const item = actionItemsService.findById(baseItemId);
      const targetDate = getNavigationDateFromEventId(
        eventId,
        item?.scheduledAt
      );

      if (targetDate) {
        calendarDateTouchedRef.current = true;
        setCalendarDate(targetDate);
        setScrollToTime(targetDate);
      }

      setCalendarViewMode(CALENDAR_VIEW_MODE.DAY);
      setHighlightedEventId(baseItemId);
      setTimeout(() => setHighlightedEventId(null), 2000);
    },
    [actionItemsService]
  );

  const clearDragPreview = useCallback(() => {
    if (dragPreviewKeyRef.current !== null) {
      dragPreviewKeyRef.current = null;
      setDragPreview(null);
    }
  }, []);

  const clearPointerListener = useCallback(() => {
    const handler = pointerMoveHandlerRef.current;
    if (handler) {
      window.removeEventListener('pointermove', handler);
      pointerMoveHandlerRef.current = null;
    }
  }, []);

  const stopAutoScroll = useCallback(() => {
    scrollVelocityRef.current = 0;
    if (scrollRafRef.current !== null) {
      window.cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
  }, []);

  const getCalendarGridRect = useCallback(() => {
    const container = focusZoneRef.current;
    if (!container) return null;
    const grid = container.querySelector<HTMLElement>(
      '[data-calendar-grid="true"]'
    );
    return grid?.getBoundingClientRect() ?? null;
  }, []);

  const updateDragPreviewFromPoint = useCallback(
    (point: { x: number; y: number }, activeId: string) => {
      if (activeDragIsCalendarEventRef.current) {
        return;
      }
      const container = focusZoneRef.current;
      if (!container) return;
      const containerRect = container.getBoundingClientRect();
      const isInsideContainer =
        point.x >= containerRect.left &&
        point.x <= containerRect.right &&
        point.y >= containerRect.top &&
        point.y <= containerRect.bottom;
      const gridRect = getCalendarGridRect();
      const paddedGridRect =
        gridRect && GRID_HOVER_PADDING_PX > 0
          ? {
              top: gridRect.top - GRID_HOVER_PADDING_PX,
              bottom: gridRect.bottom + GRID_HOVER_PADDING_PX,
              left: gridRect.left - GRID_HOVER_PADDING_PX,
              right: gridRect.right + GRID_HOVER_PADDING_PX,
            }
          : gridRect;
      const isInsideGrid =
        paddedGridRect &&
        point.y >= paddedGridRect.top &&
        point.y <= paddedGridRect.bottom &&
        point.x >= paddedGridRect.left &&
        point.x <= paddedGridRect.right;
      const isHoveringGrid = Boolean(isInsideGrid);
      const isHovering = Boolean(isInsideContainer || isInsideGrid);
      dragWithinGridRef.current = isHoveringGrid;

      if (dragHoveringRef.current !== isHovering) {
        dragHoveringRef.current = isHovering;
        setDragHoveringFocusZone(isHovering);
      }

      if (!isHovering) {
        dragWithinGridRef.current = false;
        clearDragPreview();
        stopAutoScroll();
        return;
      }

      if (!isHoveringGrid) {
        stopAutoScroll();
        return;
      }

      if (!gridRect || gridRect.height <= 0 || gridRect.width <= 0) {
        stopAutoScroll();
        return;
      }

      const clampedY = Math.min(
        Math.max(point.y, gridRect.top),
        gridRect.bottom
      );
      const clampedX = Math.min(
        Math.max(point.x, gridRect.left),
        gridRect.right
      );
      const rawRatio = (clampedY - gridRect.top) / gridRect.height;
      const ratio = Math.min(Math.max(rawRatio, 0), 1);
      const minutes = ratio * 24 * 60;
      const snappedMinutes =
        Math.round(minutes / DRAG_SNAP_MINUTES) * DRAG_SNAP_MINUTES;
      const hour = Math.floor(snappedMinutes / 60);
      const minute = snappedMinutes % 60;

      const baseDate =
        !hasItems && !calendarDateTouchedRef.current
          ? new Date()
          : calendarDate instanceof Date && !isNaN(calendarDate.getTime())
            ? calendarDate
            : new Date();
      let dateStr = format(baseDate, 'yyyy-MM-dd');
      if (calendarViewMode === CALENDAR_VIEW_MODE.WEEK) {
        const weekDates = getWeekDates(baseDate);
        const ratio = (clampedX - gridRect.left) / gridRect.width;
        const dayIndex = Math.min(6, Math.max(0, Math.floor(ratio * 7)));
        const targetDate = weekDates[dayIndex] ?? baseDate;
        dateStr = format(targetDate, 'yyyy-MM-dd');
      }
      const clamped = clampSlotToNow({ dateStr, hour, minute });
      setDragPreviewSlot(
        activeId,
        clamped.dateStr,
        clamped.hour,
        clamped.minute
      );
    },
    [
      calendarDate,
      calendarViewMode,
      clampSlotToNow,
      clearDragPreview,
      getDefaultAnchorSlot,
      getCalendarGridRect,
      hasItems,
      setDragPreviewSlot,
      stopAutoScroll,
    ]
  );

  const getCalendarScrollContainer = useCallback(() => {
    const container = focusZoneRef.current;
    if (!container) return null;
    return container.querySelector<HTMLElement>(
      '[data-calendar-scroll="true"]'
    );
  }, []);

  const startAutoScroll = useCallback(() => {
    if (scrollRafRef.current !== null) return;
    const step = () => {
      const scrollContainer = getCalendarScrollContainer();
      const velocity = scrollVelocityRef.current;
      if (!scrollContainer || velocity === 0) {
        scrollRafRef.current = null;
        return;
      }
      scrollContainer.scrollTop += velocity;
      const currentPoint = lastPointerRef.current;
      const currentActiveId = activeDragIdRef.current;
      if (currentPoint && currentActiveId) {
        updateDragPreviewFromPoint(currentPoint, currentActiveId);
        updateAutoScroll(currentPoint);
      }
      scrollRafRef.current = window.requestAnimationFrame(step);
    };
    scrollRafRef.current = window.requestAnimationFrame(step);
  }, [getCalendarScrollContainer, updateDragPreviewFromPoint]);

  const updateAutoScroll = useCallback(
    (point: { x: number; y: number }) => {
      if (!dragWithinGridRef.current) {
        stopAutoScroll();
        return;
      }
      const scrollContainer = getCalendarScrollContainer();
      if (!scrollContainer) return;
      const rect = scrollContainer.getBoundingClientRect();
      if (
        point.y < rect.top - SCROLL_EDGE_PX ||
        point.y > rect.bottom + SCROLL_EDGE_PX
      ) {
        stopAutoScroll();
        return;
      }
      const topDistance = point.y - rect.top;
      const bottomDistance = rect.bottom - point.y;
      let velocity = 0;
      if (topDistance < SCROLL_EDGE_PX) {
        const ratio = Math.min(
          Math.max((SCROLL_EDGE_PX - topDistance) / SCROLL_EDGE_PX, 0),
          1
        );
        velocity = -Math.max(1, Math.round(SCROLL_SPEED_PX * ratio));
      } else if (bottomDistance < SCROLL_EDGE_PX) {
        const ratio = Math.min(
          Math.max((SCROLL_EDGE_PX - bottomDistance) / SCROLL_EDGE_PX, 0),
          1
        );
        velocity = Math.max(1, Math.round(SCROLL_SPEED_PX * ratio));
      }
      if (scrollVelocityRef.current === velocity) {
        if (velocity === 0) {
          stopAutoScroll();
        } else {
          startAutoScroll();
        }
        return;
      }
      scrollVelocityRef.current = velocity;
      if (velocity === 0) {
        stopAutoScroll();
      } else {
        startAutoScroll();
      }
    },
    [getCalendarScrollContainer, startAutoScroll, stopAutoScroll]
  );

  useEffect(() => {
    return () => {
      clearPointerListener();
      stopAutoScroll();
    };
  }, [clearPointerListener, stopAutoScroll]);

  const handleCalendarSlotDrop = useCallback(
    (itemId: string, dateStr: string, hour: number, minute: number) => {
      const item = actionItemsService.findById(itemId);
      if (!item) return;
      const clamped = clampSlotToNow({ dateStr, hour, minute });
      const scheduledDate = buildSlotDate(
        clamped.dateStr,
        clamped.hour,
        clamped.minute
      );
      if (!scheduledDate) return;
      const plannedMinutes = getPlannedFocusMinutes(item);

      updateScheduledFocusItem(itemId, scheduledDate, plannedMinutes);

      calendarDateTouchedRef.current = true;
      setScrollToTime(scheduledDate);
      if (calendarViewMode !== CALENDAR_VIEW_MODE.WEEK) {
        setCalendarDate(scheduledDate);
      }
    },
    [
      actionItemsService,
      calendarViewMode,
      clampSlotToNow,
      updateScheduledFocusItem,
    ]
  );

  const handleCalendarDateChange = useCallback((date: Date) => {
    calendarDateTouchedRef.current = true;
    setCalendarDate(prev => {
      const prevKey = format(prev, 'yyyy-MM-dd');
      const nextKey = format(date, 'yyyy-MM-dd');
      if (prevKey === nextKey) return prev;
      return date;
    });
  }, []);

  const handleJumpToToday = useCallback(() => {
    const today = new Date();
    calendarDateTouchedRef.current = true;
    setCalendarDate(today);
    setScrollToTime(today);
  }, []);

  const handleDisplayModeChange = useCallback(
    (mode: string) => {
      setDisplayMode(mode as FocusZoneDisplayMode);
      posthog.capture('focus_zone_display_mode_changed', { mode });
    },
    [setDisplayMode]
  );

  const displayModeItems = useMemo(
    () => [
      {
        value: 'list',
        label: (
          <HugeiconsIcon icon={CheckListIcon} size={14} strokeWidth={1.8} />
        ),
        style: { width: 26, padding: 0 },
      },
      {
        value: 'calendar',
        label: (
          <HugeiconsIcon icon={Calendar03Icon} size={14} strokeWidth={1.8} />
        ),
        style: { width: 26, padding: 0 },
      },
    ],
    []
  );

  const isListMode = displayMode === 'list';

  useEffect(() => {
    if (!scrollToTime) return;
    const rafId = window.requestAnimationFrame(() => {
      setScrollToTime(undefined);
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [scrollToTime]);

  useEffect(() => {
    if (!draftEvent) return;
    const rafId = window.requestAnimationFrame(() => {
      draftInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [draftEvent?.id]);

  useDndMonitor({
    onDragStart: event => {
      const activeId = String(event.active.id);
      if (!actionItemById.has(activeId)) return;
      clearDragPreview();
      clearPointerListener();
      dragHoveringRef.current = false;
      dragWithinGridRef.current = false;
      setDragHoveringFocusZone(false);
      activeDragIdRef.current = activeId;
      const isCalendarEvent = Boolean(
        (event.active.data.current as { event?: CalendarEvent } | undefined)
          ?.event
      );
      activeDragIsCalendarEventRef.current = isCalendarEvent;
      setActiveDragId(activeId);
      setDraggingFromList(!isCalendarEvent);
      const rect =
        event.active.rect.current.translated ??
        event.active.rect.current.initial;
      if (rect) {
        const point = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        };
        lastPointerRef.current = point;
        if (!activeDragIsCalendarEventRef.current) {
          updateDragPreviewFromPoint(point, activeId);
        }
        updateAutoScroll(point);
      }
    },
    onDragMove: event => {
      const activeId = String(event.active.id);
      if (!actionItemById.has(activeId)) return;
      const rect =
        event.active.rect.current.translated ??
        event.active.rect.current.initial;
      if (!rect) return;
      const point = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
      lastPointerRef.current = point;
      if (!activeDragIsCalendarEventRef.current) {
        updateDragPreviewFromPoint(point, activeId);
      }
      updateAutoScroll(point);
    },
    onDragOver: event => {
      const overId = String(event.over?.id ?? '');
      if (!overId.startsWith(CALENDAR_DROP_PREFIX)) return;
      const activeId = String(event.active.id);
      if (!actionItemById.has(activeId)) return;
      if (activeDragIsCalendarEventRef.current) return;
      const slot = parseCalendarSlotId(overId, CALENDAR_DROP_PREFIX);
      if (!slot) return;
      const clamped = clampSlotToNow(slot);
      dragHoveringRef.current = true;
      setDragHoveringFocusZone(true);
      setDragPreviewSlot(
        activeId,
        clamped.dateStr,
        clamped.hour,
        clamped.minute
      );
    },
    onDragEnd: event => {
      const activeId = String(event.active.id);
      const preview = dragPreviewRef.current;
      const isValidDrop =
        preview &&
        preview.itemId === activeId &&
        actionItemById.has(activeId) &&
        dragHoveringRef.current;
      if (isValidDrop) {
        const clamped = clampSlotToNow({
          dateStr: preview.dateStr,
          hour: preview.hour,
          minute: preview.minute,
        });
        const scheduledDate = buildSlotDate(
          clamped.dateStr,
          clamped.hour,
          clamped.minute
        );

        if (scheduledDate) {
          const item = actionItemById.get(activeId);
          // 1. Commit Optimistic State using CLAMPED values
          committedPreviewRef.current = {
            itemId: activeId,
            scheduledAt: scheduledDate.toISOString(),
            plannedDuration: item
              ? getPlannedFocusMinutes(item)
              : DEFAULT_PLANNED_FOCUS_MINUTES,
          };

          // 2. Trigger Backend Update (Deferred) using CLAMPED values
          setTimeout(() => {
            handleCalendarSlotDrop(
              activeId,
              clamped.dateStr,
              clamped.hour,
              clamped.minute
            );
          }, 0);
        }
      }
      clearPointerListener();
      stopAutoScroll();
      clearDragPreview();
      dragHoveringRef.current = false;
      dragWithinGridRef.current = false;
      setDragHoveringFocusZone(false);
      activeDragIdRef.current = null;
      activeDragIsCalendarEventRef.current = false;
      lastPointerRef.current = null;
      setActiveDragId(null);
      setDraggingFromList(false);
    },
    onDragCancel: () => {
      clearPointerListener();
      stopAutoScroll();
      clearDragPreview();
      committedPreviewRef.current = null;
      dragHoveringRef.current = false;
      dragWithinGridRef.current = false;
      setDragHoveringFocusZone(false);
      activeDragIdRef.current = null;
      activeDragIsCalendarEventRef.current = false;
      lastPointerRef.current = null;
      setActiveDragId(null);
      setDraggingFromList(false);
    },
  });

  useEffect(() => {
    if (!dragHoveringFocusZone) return;
    let frameCount = 0;
    let rafId = 0;
    const tick = () => {
      frameCount += 1;
      const currentPoint = lastPointerRef.current;
      const currentActiveId = activeDragIdRef.current;
      if (currentPoint && currentActiveId) {
        updateDragPreviewFromPoint(currentPoint, currentActiveId);
      }
      if (!getCalendarGridRect() && frameCount < 5) {
        rafId = window.requestAnimationFrame(tick);
      }
    };
    rafId = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [
    dragHoveringFocusZone,
    getCalendarGridRect,
    updateDragPreviewFromPoint,
    updateAutoScroll,
  ]);

  useEffect(() => {
    if (dragHoveringFocusZone) {
      if (!dragDefaultedRef.current) {
        const today = new Date();
        setCalendarDate(today);
        setScrollToTime(today);
        setCalendarViewMode(CALENDAR_VIEW_MODE.DAY);
        dragDefaultedRef.current = true;
      }
      return;
    }
    dragDefaultedRef.current = false;
  }, [dragHoveringFocusZone]);

  useEffect(() => {
    if (!dragHoveringFocusZone) return;
    setScrollToTime(new Date());
  }, [dragHoveringFocusZone]);

  useEffect(() => {
    if (focusedItemId) return;
    const scheduledItems = focusedItems.filter(item => {
      if (!item.scheduledAt) return false;
      if (isActionItemCompleted(item)) return false;
      return true;
    });
    if (scheduledItems.length === 0) return;

    const checkScheduledItems = () => {
      const nowMs = Date.now();
      const windowMs = 2 * 60 * 1000;

      for (const item of scheduledItems) {
        if (!item.scheduledAt) continue;
        const start = new Date(item.scheduledAt);
        if (isNaN(start.getTime())) continue;

        const startMs = start.getTime();
        const durationMs = getPlannedFocusMinutes(item) * 60_000;
        const endMs = startMs + durationMs;

        if (nowMs < startMs || nowMs >= endMs) continue;
        if (nowMs > startMs + windowMs) continue;

        const notifyKey = `${item.id}:${item.scheduledAt}`;
        if (notifiedScheduledItemsRef.current.has(notifyKey)) continue;

        notifiedScheduledItemsRef.current.add(notifyKey);
        const title =
          typeof item.title === 'string' ? item.title : 'Scheduled task';
        const deeplinkUrl = `pique://focus-timer/${item.id}?t=${Date.now()}`;

        void enhancedNotify.success(
          {
            title: 'Time to focus',
            message: `"${title}" is scheduled now`,
            url: deeplinkUrl,
            actionLabel: 'Start Timer',
            sound: false,
            timeout: 15000,
            action: {
              label: 'Start Timer',
              onClick: () => {
                actionItemsService.setFocusedItem(item.id);
              },
              autoClose: true,
            },
          },
          { duration: 15000 }
        );
      }
    };

    checkScheduledItems();
    const intervalId = window.setInterval(checkScheduledItems, 30_000);
    return () => window.clearInterval(intervalId);
  }, [actionItemsService, focusedItemId, focusedItems]);

  const renderCalendarEvent = useCallback(
    ({ event, isDragging, isResizing }: EventRenderProps) => {
      if (draftEvent && event.id === draftEvent.id) {
        return (
          <div className={styles.draftEventCard}>
            <input
              ref={draftInputRef}
              className={styles.draftEventInput}
              value={draftTitle}
              onChange={e => setDraftTitle(e.target.value)}
              onKeyDown={handleDraftKeyDown}
              onBlur={handleDraftBlur}
              placeholder="Add task..."
              aria-label="Action item title"
            />
            <button
              type="button"
              className={styles.draftDiscardButton}
              onClick={e => {
                e.stopPropagation();
                clearDraftEvent();
              }}
              onMouseDown={e => e.preventDefault()}
              title="Discard"
            >
              <HugeiconsIcon icon={Cancel01Icon} size={12} />
            </button>
          </div>
        );
      }
      const item = getItemFromEventId(event.id);
      if (!item) return null;
      const isRepeatInstance = event.id.includes(':repeat:');
      const isInteracting = isDragging || isResizing;
      const isPreview = Boolean(event.isPreview);
      const previewScheduledAt =
        isDragging || isPreview ? getEventStartIso(event) : null;
      const displayItem =
        previewScheduledAt && previewScheduledAt !== item.scheduledAt
          ? { ...item, scheduledAt: previewScheduledAt }
          : item;
      const plannedDurationMinutes = getEventDurationMinutes(event);

      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            pointerEvents: isInteracting ? 'none' : 'auto',
          }}
        >
          <TodoItemComponent
            item={displayItem}
            onComplete={onItemComplete}
            compact={true}
            isDraggable={false}
            calendarEvent={true}
            calendarEventId={event.id}
            dragging={isInteracting}
            isResizing={isResizing}
            plannedDurationMinutes={
              plannedDurationMinutes ?? item.plannedDuration ?? undefined
            }
            onClearSchedule={isRepeatInstance ? undefined : handleClearSchedule}
            onExpandToWeek={
              calendarViewMode === CALENDAR_VIEW_MODE.WEEK
                ? undefined
                : handleExpandToWeek
            }
            onCollapseToDay={
              calendarViewMode === CALENDAR_VIEW_MODE.WEEK
                ? handleCollapseToDay
                : undefined
            }
            isHighlighted={highlightedEventId === item.id}
          />
        </div>
      );
    },
    [
      calendarViewMode,
      clearDraftEvent,
      draftEvent,
      draftTitle,
      getItemFromEventId,
      handleDraftBlur,
      handleDraftKeyDown,
      onItemComplete,
      handleClearSchedule,
      handleExpandToWeek,
      handleCollapseToDay,
      highlightedEventId,
    ]
  );

  const showCalendarSurface = isListMode || hasItems || isFocusZoneActive;
  const calendarMotionProps = hasItems
    ? {
        initial: reduceMotion ? false : { opacity: 0, scale: 0.992 },
        animate: reduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1 },
        exit: reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.992 },
        transition: reduceMotion
          ? { duration: 0 }
          : { duration: 0.16, ease: [0.22, 1, 0.36, 1] },
      }
    : {
        initial: false,
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        transition: { duration: 0 },
      };

  return (
    <div
      ref={setFocusZoneRef}
      className={styles.focusDropZoneContainer}
      data-testid="focus-drop-zone"
      data-active={isFocusZoneActive ? true : undefined}
      data-has-item={hasItems || undefined}
      data-expanded={showCalendarSurface || undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={styles.focusDropZoneHeader}
        data-testid="focus-mode-header"
      >
        {!showCalendarSurface && !isEmptyState && (
          <div className={styles.focusDropZoneContent}>
          </div>
        )}
        {showCalendarSurface && (
          <div className={styles.displayModeToggle}>
            <RadioGroup
              value={displayMode}
              onChange={handleDisplayModeChange}
              items={displayModeItems}
              iconMode
              padding={3}
              borderRadius={16}
              gap={2}
              itemHeight={22}
              style={{ border: 'none', boxShadow: 'none', backgroundColor: 'transparent', padding: 0, gap: 0 }}
            />
          </div>
        )}
      </div>


      {isListMode && (
        <ActionItemFocusList
          items={focusedItems}
          onItemComplete={onItemComplete}
          dropIndicatorVisible={isFocusZoneActive}
          dropIndicatorItem={dropIndicatorItem}
        />
      )}

      <AnimatePresence initial={false} mode="popLayout">
        {showCalendarSurface && !isListMode ? (
          <motion.div
            key="focus-calendar"
            className={styles.focusCalendarWrapper}
            {...calendarMotionProps}
          >
            <CalendarScheduler
              events={combinedCalendarEvents}
              onEventCreate={handleEventCreate}
              onEventUpdate={handleEventUpdate}
              onEventDelete={handleEventDelete}
              initialDate={calendarDate}
              onDateChange={handleCalendarDateChange}
              onToday={handleJumpToToday}
              viewMode={calendarViewMode}
              onViewModeChange={setCalendarViewMode}
              compact={true}
              renderEvent={renderCalendarEvent}
              scrollToTime={scrollToTime}
              dragSnapMinutes={DRAG_SNAP_MINUTES}
              alwaysShowCurrentTime={true}
              externalDropIdPrefix={CALENDAR_DROP_PREFIX}
              suppressSlotHover={dragHoveringFocusZone || !!draftEvent}
              enableWeekView={true}
              hideToolbar={false}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>


      {pendingRecurringUpdate && (
        <div
          className={styles.recurringUpdateDialog}
          onClick={handleCancelRecurringUpdate}
        >
          <div
            className={styles.recurringUpdateDialogContent}
            onClick={e => e.stopPropagation()}
          >
            <h3 className={styles.recurringUpdateDialogTitle}>
              Update Recurring Event
            </h3>
            <p className={styles.recurringUpdateDialogText}>
              This will change the time for all future occurrences of this
              recurring task to {pendingRecurringUpdate.newTime}.
            </p>
            <div className={styles.recurringUpdateDialogButtons}>
              <button
                type="button"
                className={styles.recurringUpdateDialogButton}
                onClick={handleCancelRecurringUpdate}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.recurringUpdateDialogButton}
                data-primary
                onClick={handleConfirmRecurringUpdate}
              >
                Update All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface DragHintRowProps {
  visible: boolean;
}

export const DragHintRow: React.FC<DragHintRowProps> = ({ visible }) => {
  return (
    <div className={styles.dragHintRow} data-visible={visible || undefined}>
      <HugeiconsIcon icon={ArrowUp01Icon} size={14} />
      <span>Drag action item to schedule</span>
    </div>
  );
};
